import { useEffect, useRef, useState } from "react";
import { Move, RotateCcw } from "lucide-react";

/**
 * ImagePositioner — lets the user drag an image inside a fixed-aspect frame
 * to control its `object-position` when displayed later.
 *
 * Emits a CSS-style position string like "50% 30%" via `onChange`.
 *
 * Props:
 *   src        — image URL
 *   value      — current "x% y%" string (defaults to "50% 50%")
 *   aspect     — CSS aspect-ratio for the preview frame (default "3 / 1" for covers)
 *   rounded    — extra Tailwind class for corners (default "rounded-3xl")
 *   circular   — if true, previews as a circle (used for avatars)
 *   onChange   — (newValue: string) => void
 */
export default function ImagePositioner({
  src, value = "50% 50%", aspect = "3 / 1",
  rounded = "rounded-3xl", circular = false, onChange, testid = "image-positioner",
}) {
  const boxRef = useRef(null);
  const dragging = useRef(false);
  const [pos, setPos] = useState(() => parsePercent(value));

  useEffect(() => { setPos(parsePercent(value)); }, [value]);

  const updateFromPointer = (e) => {
    const box = boxRef.current?.getBoundingClientRect();
    if (!box) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const x = clamp(((clientX - box.left) / box.width) * 100, 0, 100);
    const y = clamp(((clientY - box.top)  / box.height) * 100, 0, 100);
    const next = { x: Math.round(x), y: Math.round(y) };
    setPos(next);
    onChange?.(`${next.x}% ${next.y}%`);
  };

  const onDown = (e) => {
    dragging.current = true;
    updateFromPointer(e);
    e.preventDefault();
  };
  const onMove = (e) => { if (dragging.current) updateFromPointer(e); };
  const onUp = () => { dragging.current = false; };

  useEffect(() => {
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchend", onUp);
    };
  }, []);

  const reset = () => {
    setPos({ x: 50, y: 50 });
    onChange?.("50% 50%");
  };

  const posStr = `${pos.x}% ${pos.y}%`;

  return (
    <div className="w-full" data-testid={testid}>
      <div
        ref={boxRef}
        onMouseDown={onDown}
        onMouseMove={onMove}
        onTouchStart={onDown}
        onTouchMove={onMove}
        className={`relative overflow-hidden bg-[#F2F2F0] ${rounded} ${circular ? "aspect-square rounded-full max-w-[220px] mx-auto" : ""} cursor-grab active:cursor-grabbing select-none`}
        style={circular ? undefined : { aspectRatio: aspect }}
        aria-label="Drag the image to reposition"
        data-testid={`${testid}-frame`}
      >
        {src ? (
          <img src={src} alt=""
               className="absolute inset-0 w-full h-full object-cover pointer-events-none"
               style={{ objectPosition: posStr }}
               draggable={false}/>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-[#8A8A8A] text-sm">
            Upload an image first
          </div>
        )}
        {/* Crosshair */}
        {src && (
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute" style={{ left: `${pos.x}%`, top: `${pos.y}%`, transform: "translate(-50%, -50%)" }}>
              <div className="h-8 w-8 rounded-full border-2 border-white shadow-lg ring-2 ring-black/20"/>
            </div>
          </div>
        )}
        {/* Hint */}
        {src && (
          <div className="absolute bottom-2 left-2 text-[10px] uppercase tracking-wider text-white bg-black/50 px-2 py-1 rounded-full inline-flex items-center gap-1">
            <Move size={10}/> Drag to reposition
          </div>
        )}
      </div>
      <div className="flex items-center justify-between mt-2">
        <p className="text-[11px] text-[#8A8A8A]" data-testid={`${testid}-value`}>Focus: {posStr}</p>
        <button type="button" onClick={reset}
                className="btn-ghost !py-1 !px-2 text-[11px]"
                data-testid={`${testid}-reset`}>
          <RotateCcw size={11}/> Center
        </button>
      </div>
    </div>
  );
}

function parsePercent(v) {
  if (!v || typeof v !== "string") return { x: 50, y: 50 };
  const parts = v.trim().split(/\s+/);
  const x = parseFloat(parts[0]);
  const y = parseFloat(parts[1] ?? parts[0]);
  return {
    x: isFinite(x) ? clamp(x, 0, 100) : 50,
    y: isFinite(y) ? clamp(y, 0, 100) : 50,
  };
}
function clamp(n, min, max) { return Math.min(max, Math.max(min, n)); }
